import { useId, useMemo, type CSSProperties, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useInRouterContext } from 'react-router';
import { useLocale } from '@/i18n/useLocale';
import { paths } from '@/app/paths';
import s from './ui.module.css';
import { cx } from './cx';
import { TOKEN_CSS, tokenMarkup } from '@/art/token';
import { useDisplayCurrency } from '@/currency/store';
import { CurrencyIcon } from '@/currency/icon';
import { sound } from '@/fx/sound';

/* ---------------- Panel ---------------- */
export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  /** right side of the header (counter, link, badge) */
  aside?: ReactNode;
  /** 14px title instead of 16px (right rails) */
  small?: boolean;
  footer?: ReactNode;
  /** 1px accent frame, e.g. "var(--orange)" */
  accent?: string;
  padded?: boolean;
  as?: 'section' | 'div' | 'aside' | 'article';
}

/** Panel: --panel background, 1px --border, optional header row / footer row. */
export function Panel({ title, aside, small, footer, accent, padded, as = 'section', className, style, children, ...rest }: PanelProps) {
  const Tag = as;
  const st: CSSProperties | undefined = accent ? ({ ...style, ['--accent' as string]: accent } as CSSProperties) : style;
  return (
    <Tag className={cx(s.panel, accent && s.panelAccent, className)} style={st} {...rest}>
      {(title !== undefined || aside !== undefined) && (
        <header className={s.panelHead}>
          {title !== undefined && <span className={small ? s.panelTitleSm : s.panelTitle}>{title}</span>}
          <span className={s.panelSpacer} />
          {aside}
        </header>
      )}
      {padded ? <div className={s.panelBody}>{children}</div> : children}
      {footer !== undefined && <footer className={s.panelFoot}>{footer}</footer>}
    </Tag>
  );
}

/* ---------------- Cap (player-facing label: UI font 12px, sentence case) ---------------- */
export function Cap({ children, muted, className, style }: { children: ReactNode; muted?: boolean; className?: string; style?: CSSProperties }) {
  return (
    <span className={cx(s.cap, muted && s.capMuted, className)} style={style}>
      {children}
    </span>
  );
}

/* ---------------- Coin (the in-game token) ---------------- */
/** The token icon (art/token.ts): follows the theme via var(--orange); `color` re-tints it. Decorative. */
export function Coin({ size = 14, color, className }: { size?: number; color?: string; className?: string }) {
  const uid = `tk${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const profile = useDisplayCurrency();
  const small = size < 24;
  const html = useMemo(() => tokenMarkup(TOKEN_CSS, uid, 64, 64, 1, small), [uid, small]);
  if (profile.enabled && profile.icon !== 'leyo') return <CurrencyIcon profile={profile} size={size} className={className} />;
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 128 128"
      width={size}
      height={size}
      className={cx(s.coin, className)}
      style={color ? ({ ['--orange' as string]: color } as CSSProperties) : undefined}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/* ---------------- Tabs / Segmented ---------------- */
export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** e.g. lang="zh" for 中文 label font */
  lang?: string;
  disabled?: boolean;
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[];
  value: T;
  onChange(v: T): void;
  /** accessible name of the group */
  label?: string;
  mono?: boolean;
  size?: 'sm' | 'md';
  variant?: 'boxed' | 'underline';
  className?: string;
}

/** Segmented control / tabs (Sign in | Create account, BET 1 | BET 2, Dark | Light | System, EN DE 中文 RU). */
export function Tabs<T extends string>({ items, value, onChange, label, mono, size = 'md', variant = 'boxed', className }: TabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx(s.tabs, mono && s.tabsMono, size === 'sm' && s.tabsSm, variant === 'underline' && s.tabsUnderline, className)}
    >
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          lang={it.lang}
          aria-selected={it.value === value}
          disabled={it.disabled}
          className={s.tab}
          onClick={() => {
            if (it.value !== value) sound.play('common/toggle');
            onChange(it.value);
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

/* ---------------- Toggle ---------------- */
export interface ToggleProps {
  checked: boolean;
  onChange(v: boolean): void;
  /** visible label (row layout) — omit for a bare switch with aria-label */
  label?: ReactNode;
  ariaLabel?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
}

/** Pill switch (the only rounded control besides avatars/coins). */
export function Toggle({ checked, onChange, label, ariaLabel, size = 'md', disabled, className }: ToggleProps) {
  const id = useId();
  const sw = (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label === undefined ? ariaLabel : undefined}
      aria-labelledby={label !== undefined ? `${id}-l` : undefined}
      disabled={disabled}
      className={cx(s.toggle, size === 'sm' && s.toggleSm)}
      onClick={() => {
        sound.play('common/toggle');
        onChange(!checked);
      }}
    >
      <span className={s.knob} />
    </button>
  );
  if (label === undefined) return sw;
  // the label wraps the switch: a click anywhere on the row (the text included) is a click on the switch
  return (
    <label className={cx(s.toggleRow, className)} htmlFor={id}>
      <span id={`${id}-l`}>{label}</span>
      {sw}
    </label>
  );
}

/* ---------------- Table ---------------- */
export interface Column<R> {
  key: string;
  header: ReactNode;
  /** CSS grid track, e.g. "1fr", "70px" */
  width?: string;
  align?: 'left' | 'right' | 'center';
  mono?: boolean;
  render(row: R, index: number): ReactNode;
  cellStyle?(row: R): CSSProperties | undefined;
}

export interface TableProps<R> {
  columns: Column<R>[];
  rows: R[];
  rowKey(row: R, i: number): string;
  highlight?(row: R): boolean;
  empty?: ReactNode;
  className?: string;
  /** hide the header row */
  headless?: boolean;
}

/** Data table: mono caps header, 1px --row dividers, grid columns (no <table> so rows can animate). */
export function Table<R>({ columns, rows, rowKey, highlight, empty, className, headless }: TableProps<R>) {
  const tpl = columns.map((c) => c.width ?? '1fr').join(' ');
  const al = (a?: 'left' | 'right' | 'center') => (a === 'right' ? s.right : a === 'center' ? s.center : undefined);
  return (
    <div role="table" className={cx(s.table, className)}>
      {!headless && (
        <div role="row" className={s.thead} style={{ gridTemplateColumns: tpl }}>
          {columns.map((c) => (
            <span role="columnheader" key={c.key} className={cx(s.cell, al(c.align))}>
              {c.header}
            </span>
          ))}
        </div>
      )}
      {rows.length === 0 && empty !== undefined ? <div className={s.empty}>{empty}</div> : null}
      {rows.map((r, i) => (
        <div role="row" key={rowKey(r, i)} className={cx(s.tr, highlight?.(r) && s.trHighlight)} style={{ gridTemplateColumns: tpl }}>
          {columns.map((c) => (
            <span role="cell" key={c.key} className={cx(s.cell, al(c.align), c.mono && s.num)} style={c.cellStyle?.(r)}>
              {c.render(r, i)}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Field ---------------- */
export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: ReactNode;
  /** right side of the label row (e.g. "Forgot password?") */
  labelAside?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export function Field({ label, labelAside, hint, error, className, type, id, ...rest }: FieldProps) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={cx(s.field, className)}>
      <div className={s.fieldHead}>
        <label htmlFor={fid} className={s.fieldLabel}>
          {label}
        </label>
        {labelAside}
      </div>
      <input
        id={fid}
        type={type}
        className={cx(s.input, type === 'password' && s.inputPassword)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${fid}-h` : undefined}
        {...rest}
      />
      {(error || hint) && (
        <span id={`${fid}-h`} className={cx(s.hint, error ? s.hintError : undefined)}>
          {error || hint}
        </span>
      )}
    </div>
  );
}

/* ---------------- Currency notice (profile and account-related screens) ---------------- */
/** Currency notice; `privacy` appends links to the privacy and cookie pages. */
export function Disclaimer({ className, style, privacy = true }: { className?: string; style?: CSSProperties; privacy?: boolean }) {
  return (
    <p className={cx(s.disclaimer, className)} style={{ margin: 0, ...style }}>
      <span>
        <FormattedMessage id="common.disclaimer" />
      </span>
      {privacy && <DisclaimerPrivacy />}
    </p>
  );
}

function DisclaimerPrivacy() {
  // the disclaimer is also rendered outside the router (tests, isolated widgets) — no link there
  return useInRouterContext() ? <DisclaimerPrivacyLink /> : null;
}

function DisclaimerPrivacyLink() {
  const l = useLocale();
  return (
    <>
      {' · '}
      <Link to={paths.privacy(l)} className={s.disclaimerLink}>
        <FormattedMessage id="privacy.title" />
      </Link>
      {' · '}
      <Link to={paths.cookies(l)} className={s.disclaimerLink}>
        <FormattedMessage id="cookies.link" />
      </Link>
    </>
  );
}

/* ---------------- Avatar ---------------- */
export function Avatar({ name, size = 34, className }: { name: string; size?: number; className?: string }) {
  const letter = Array.from(name.trim())[0] ?? '?';
  return (
    <span
      aria-hidden="true"
      className={cx(s.avatar, className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42), borderWidth: size >= 56 ? 3 : 2 }}
    >
      {letter}
    </span>
  );
}

export const uiClasses = s;
